// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components/macro"
import { useDispatch, useSelector } from "react-redux"
import { Trans } from "@lingui/react"
import { t } from "@lingui/macro"
import * as UserContextSlice from "../../user-context/slice"
import Notification from "../../shared/Notification"
import { useClickOutside } from "../../shared/useClickOutside"
import SvgIcon from "../../shared/SvgIcon"
import { AutoOpenNotificationsPane } from "../../user-context/slice"
import { prepareAllNotifications } from "./prepareAllNotifications"

const Wrapper = styled.div<{ isRestrictedTeamWithNotifications: boolean }>`
  position: relative;
  margin-left: 1rem;

  // Required for restricted team overlay, so notifications will be accessible
  ${({ isRestrictedTeamWithNotifications }) => isRestrictedTeamWithNotifications && css`
    z-index: 104;
  `};
`

const ButtonActive = css`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.indigo};
`

const Button = styled.button<{ isActive: boolean }>`
  position: relative;
  height: 30px;
  padding: 5px 7px;
  border-radius: 3px;
  transition: all 300ms ease;
  
  &:hover {
    ${ButtonActive};
  }
  
  ${({ isActive }) => isActive && css`
    ${ButtonActive};
  `}
`

const Count = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  color:  ${({ theme }) => theme.colors.white};
  text-align: center;
  font-size: 1rem;
  border-radius: 7px;
  min-width: 15px;
  height: 15px;
  line-height: 14px;
  padding: 0 4px;
  background-color:  ${({ theme }) => theme.colors.bittersweet};
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 100, 100, 0.7);
    }
  
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(255, 100, 100, 0);
    }
  
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 100, 100, 0);
    }
  }
`

const NotificationsOverlay = styled.div`
  width: 374px;
  position: absolute;
  top: 34px;
  right: 0;
  z-index: 2;
  border: 1px solid ${({ theme }) => theme.colors.gainsboro};
  background-color: ${({ theme }) => theme.colors.lightYellow};
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .15);
  max-height: calc(100vh - 100px);
  max-height: calc(100dvh - 100px);
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.screen.xxs }) {
    right: -40px;
    width: calc(100vw - 20px);
  }
`

const Message = styled.p`
  margin: 2rem;
`

const Notifications = (): React.ReactElement => {
  const dispatch = useDispatch()
  const [isActive, setIsActive] = useState(false)
  const generalNotifications = useSelector(UserContextSlice.selectGeneralNotifications)
  const siteNotifications = useSelector(UserContextSlice.selectSiteNotifications)
  const autoOpenNotificationsPane = useSelector(UserContextSlice.selectAutoOpenNotificationsPane)
  const isRestrictedTeamWithNotifications = useSelector(UserContextSlice.selectIsRestrictedTeamWithNotifications)
  const notifications = prepareAllNotifications(generalNotifications, siteNotifications)
  const count = notifications.length
  const ref = useRef(null)

  useClickOutside(
    ref.current,
    isActive,
    () => setIsActive(false),
  )

  useEffect(() => {
    if (autoOpenNotificationsPane === AutoOpenNotificationsPane.INITIAL && count > 0) {
      setIsActive(true)
      dispatch(UserContextSlice.setAutoOpenNotificationsPaneToOpened())
    }
  }, [autoOpenNotificationsPane, count])

  return (
    <Wrapper ref={ref} isRestrictedTeamWithNotifications={isRestrictedTeamWithNotifications}>
      <Button
        type="button"
        isActive={isActive}
        title={t({ id: "label_notifications", message: "Notifications" })}
        onClick={() => setIsActive(!isActive)}
      >
        <SvgIcon name="bell"/>
        {count > 0 && <Count>{count}</Count>}
      </Button>
      {isActive && (
        <NotificationsOverlay>
          {notifications.length ?
            notifications.map(notification => <Notification key={notification.id} notification={notification}/>) :
            (
              <Message>
                <Trans id="label_no_notifications" message="No notifications"/>
              </Message>
            )
          }
        </NotificationsOverlay>
      )}
    </Wrapper>
  )
}

export default Notifications
